/**
 * PURPOSE: Textos de interfaz del módulo de Resultados de Atletismo (solo presentación).
 * RESPONSIBILITIES: Traducir los códigos del contrato (estados, formatos, medallas, cronometraje) a
 *   etiquetas en español y dar formato corto a fechas y horarios.
 * COLLABORATORS: componentes de area/AtletismoResultadosArea.
 * ROUTE: winscore/src/area/AtletismoResultadosArea/labels.js
 */

export const PRUEBA_STATUS_LABELS = Object.freeze({
  pending: "Pendiente",
  in_progress: "En curso",
  official: "Oficial",
});

export const ROUND_STATUS_OPTIONS = Object.freeze([
  { value: "pending", label: "Pendiente" },
  { value: "in_progress", label: "En curso" },
  { value: "done", label: "Terminada" },
]);

export const ROUND_STATUS_LABELS = Object.freeze({
  pending: "Pendiente",
  in_progress: "En curso",
  done: "Terminada",
});

export const ENTRY_STATUS_LABELS = Object.freeze({
  OK: "OK",
  DNS: "DNS",
  DNF: "DNF",
  DQ: "DQ",
  NM: "NM",
  NH: "NH",
});

export const ENTRY_STATUS_TITLES = Object.freeze({
  OK: "Con marca",
  DNS: "No se presentó",
  DNF: "No terminó",
  DQ: "Descalificado",
  NM: "Sin marca válida",
  NH: "Sin altura válida",
});

export const RESULTS_VISIBILITY_OPTIONS = Object.freeze([
  { value: "off", label: "Ocultos" },
  { value: "live", label: "En vivo" },
  { value: "official", label: "Solo oficiales" },
]);

export const RESULTS_VISIBILITY_LABELS = Object.freeze({
  off: "Resultados ocultos en Hayllis 2.0",
  live: "Resultados en vivo en Hayllis 2.0",
  official: "Resultados públicos solo cuando sea oficial",
});

export const MEDAL_LABELS = Object.freeze({ 1: "Oro", 2: "Plata", 3: "Bronce" });

/** Advertencias de inscripción del servidor (no bloquean). */
export const WARNING_LABELS = Object.freeze({
  INSTITUTION_LIMIT: "Límite por universidad",
  ATHLETE_EVENT_LIMIT: "Límite de pruebas por atleta",
  TEAM_LIMIT: "Límite de equipos",
  TEAM_INCOMPLETE: "Equipo incompleto",
  MIXED_ORDER: "Orden mixto",
  ATHLETE_RELAY_LIMIT: "Límite de postas por atleta",
});

/**
 * Motivos por los que oficializar no registró el podio (`podiumSync.reason`). El podio es el de
 * Resultados individuales (FTR-0057: Oro/Plata/Bronce de la prueba); NO escribe el medallero general.
 */
export const PODIUM_SYNC_REASONS = Object.freeze({
  INDIVIDUAL_RESULTS_DISABLED: "el registro de podios individuales está apagado",
  NOT_SUPPORTED: "esta prueba no registra podio",
  TIES_NOT_SUPPORTED: "hay empates en el podio; regístralo manualmente",
  PODIUM_ALREADY_OFFICIAL: "el podio ya estaba registrado como oficial",
});

/** Texto del aviso tras oficializar según `podiumSync`; null si el servidor no lo informó. */
export const describePodiumSync = (podiumSync) => {
  if (!podiumSync || typeof podiumSync !== "object") return null;
  if (podiumSync.synced === true) {
    return { ok: true, message: "Podio registrado en Resultados (Oro/Plata/Bronce)." };
  }
  const reason = podiumSync.reason;
  return {
    ok: false,
    message: `El podio no se registró: ${PODIUM_SYNC_REASONS[reason] || reason || "motivo no informado"}.`,
  };
};

export const TIMING_LABELS = Object.freeze({
  electronic: "Cronometraje electrónico",
  manual: "Cronometraje manual",
});

const WEEKDAYS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

/** "2026-10-09" → "Vie 09/10". Texto no reconocido se devuelve tal cual. */
export const formatShortDate = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ""));
  if (!match) return value ? String(value) : "";
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return `${WEEKDAYS[date.getDay()]} ${match[3]}/${match[2]}`;
};

/** "08:50:00" → "08:50". */
export const formatTime = (value) => {
  const match = /^(\d{1,2}):(\d{2})/.exec(String(value || ""));
  return match ? `${match[1].padStart(2, "0")}:${match[2]}` : value ? String(value) : "";
};

export const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("es-PE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const participantName = (participant) => participant?.name || "Sin nombre";

export const institutionAbrev = (institution) =>
  institution?.abrev || institution?.name || "—";
